import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { getVendorOrders, updateOrderStatus, markOrderShipped, type VendorOrderItem } from "@/lib/orders.functions";

const fmtInt = new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 0 }).format;
const fmtCurrency = new Intl.NumberFormat("sl-SI", { style: "currency", currency: "EUR" }).format;

export const Route = createFileRoute("/_authenticated/prodaja")({
  head: () => ({ meta: [{ title: "Prejeta naročila — kupish.si" }] }),
  component: Page,
});

const STATUS_LABEL: Record<string, string> = {
  quote_requested: "Povpraševanje",
  quote_sent: "Predračun poslan",
  pending: "V obdelavi",
  paid: "Plačano",
  shipped: "Odposlano",
  delivered: "Dostavljeno",
  canceled: "Preklicano",
  refunded: "Povrnjeno",
  failed: "Neuspelo",
};

type Tab = "all" | "quote_requested" | "quote_sent" | "paid" | "shipped";

function Page() {
  const qc = useQueryClient();
  const fetchOrders = useServerFn(getVendorOrders);
  const updateStatus = useServerFn(updateOrderStatus);
  const shipOrder = useServerFn(markOrderShipped);
  const { data, isLoading } = useQuery({ queryKey: ["vendor-orders"], queryFn: () => fetchOrders() });
  const [busy, setBusy] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("all");
  const [shipFor, setShipFor] = useState<string | null>(null);
  const [trackingNum, setTrackingNum] = useState("");
  const [trackingCar, setTrackingCar] = useState("");

  const setStatus = async (order_id: string, status: "paid" | "canceled" | "refunded" | "quote_sent" | "pending" | "delivered") => {
    setBusy(order_id);
    try {
      await updateStatus({ data: { order_id, status } });
      qc.invalidateQueries({ queryKey: ["vendor-orders"] });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Napaka.");
    } finally {
      setBusy(null);
    }
  };

  const submitShip = async (order_id: string) => {
    setBusy(order_id);
    try {
      await shipOrder({ data: { order_id, tracking_number: trackingNum.trim() || null, tracking_carrier: trackingCar.trim() || null } });
      setShipFor(null);
      setTrackingNum("");
      setTrackingCar("");
      qc.invalidateQueries({ queryKey: ["vendor-orders"] });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Napaka.");
    } finally {
      setBusy(null);
    }
  };

  const groups: Record<string, VendorOrderItem[]> = {};
  for (const it of data?.items ?? []) {
    (groups[it.order_id] ||= []).push(it);
  }
  let orderIds = Object.keys(groups);
  if (tab !== "all") {
    orderIds = orderIds.filter((id) => groups[id][0].order_status === tab);
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: 0, quote_requested: 0, quote_sent: 0, paid: 0, shipped: 0 };
    for (const id of Object.keys(groups)) {
      const s = groups[id][0].order_status;
      c.all++;
      if (s in c) c[s]++;
    }
    return c;
  }, [groups]);

  const stats = useMemo(() => {
    const items = data?.items ?? [];
    const paidStatuses = ["paid", "shipped", "delivered"];
    const totalRevenue = items.filter((i) => paidStatuses.includes(i.order_status)).reduce((s, i) => s + i.line_total, 0);
    const totalUnits = items.filter((i) => paidStatuses.includes(i.order_status)).reduce((s, i) => s + i.quantity, 0);
    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    const monthRevenue = items
      .filter((i) => paidStatuses.includes(i.order_status) && new Date(i.created_at) >= monthStart)
      .reduce((s, i) => s + i.line_total, 0);
    return { totalRevenue, totalUnits, monthRevenue, orderCount: Object.keys(groups).length };
  }, [data, groups]);

  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <Link to="/dashboard" className="text-xs eyebrow hover:text-accent">← Nadzorna plošča</Link>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">{data?.isAdmin ? "Vsa naročila in prodaje" : "Prejeta naročila"}</h1>
      {data?.isAdmin && <p className="mt-2 text-sm text-muted-foreground">Skrbniški pregled vseh prodaj v nakupovalnem središču.</p>}

      {!isLoading && Object.keys(groups).length > 0 && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Naročila" value={fmtInt(stats.orderCount)} />
          <StatCard label="Prodani kosi" value={fmtInt(stats.totalUnits)} />
          <StatCard label="Prihodek (vse)" value={<span className="text-accent">{fmtCurrency(stats.totalRevenue)}</span>} />
          <StatCard label="Prihodek (mesec)" value={<span className="text-accent">{fmtCurrency(stats.monthRevenue)}</span>} />
        </div>
      )}

      <div className="mt-8 flex gap-1 border-b border-border overflow-x-auto">
        {([
          ["all", "Vse"],
          ["quote_requested", "Povpraševanja"],
          ["quote_sent", "Predračuni"],
          ["paid", "Plačano"],
          ["shipped", "Odposlano"],
        ] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-3 text-sm whitespace-nowrap transition-colors ${tab === k ? "border-b-2 border-accent text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {l} {counts[k] > 0 && <span className="text-xs text-muted-foreground">({fmtInt(counts[k])})</span>}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="mt-12 text-muted-foreground">Nalagam…</div>
      ) : orderIds.length === 0 ? (
        <div className="mt-12 border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">Ni naročil v tej kategoriji.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-6">
          {orderIds.map((oid) => {
            const items = groups[oid];
            const first = items[0];
            const subtotal = items.reduce((s, i) => s + i.line_total, 0);
            const addr = first.shipping_address;
            const status = first.order_status;
            return (
              <div key={oid} className="border border-border">
                <div className="flex items-center justify-between flex-wrap gap-3 p-5 bg-muted/30 border-b border-border">
                  <div>
                    <div className="text-xs text-muted-foreground">
                      Naročilo #{oid.slice(0, 8)}
                      {first.invoice_number && <> · Račun {first.invoice_number}</>}
                    </div>
                    <div className="text-sm">
                      {first.created_at && new Date(first.created_at).toLocaleDateString("sl-SI", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{first.customer_name ?? "—"} · {first.customer_email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-wider px-2 py-1 bg-background border border-border">
                      {STATUS_LABEL[status] ?? status}
                    </span>
                    <div className="font-serif text-lg text-accent">{fmtCurrency(subtotal)}</div>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {items.map((it) => (
                    <div key={it.id} className="p-4 grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 text-sm tabular-nums">
                      <div className="min-w-0 font-medium truncate">{it.title}</div>
                      <div className="text-muted-foreground text-right whitespace-nowrap w-[7.5rem]">
                        <span className="inline-block w-6 text-right">{it.quantity}×</span>{" "}
                        <span className="text-accent inline-block w-16 text-right">{fmtCurrency(it.unit_price)}</span>
                      </div>
                      <div className="font-serif text-accent text-right whitespace-nowrap w-20">{fmtCurrency(it.line_total)}</div>
                    </div>
                  ))}
                </div>
                {addr && (
                  <div className="p-4 border-t border-border text-xs text-muted-foreground">
                    Naslov: {[addr.name, addr.line1, addr.postal_code, addr.city, addr.country].filter(Boolean).join(", ")}
                    {addr.phone && <> · {addr.phone}</>}
                  </div>
                )}
                {first.notes && (
                  <div className="p-4 border-t border-border text-xs">
                    <span className="text-muted-foreground">Opomba kupca: </span>{first.notes}
                  </div>
                )}
                {first.tracking_number && (
                  <div className="p-4 border-t border-border text-xs">
                    <span className="text-muted-foreground">Sledenje: </span>
                    {first.tracking_carrier && <span>{first.tracking_carrier} · </span>}
                    <span className="font-mono">{first.tracking_number}</span>
                  </div>
                )}

                <div className="p-4 border-t border-border flex flex-wrap gap-2">
                  {(status === "quote_requested" || status === "pending") && first.quote_id && (
                    <Link
                      to="/predracun/uredi/$id"
                      params={{ id: first.quote_id }}
                      className="bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90"
                    >
                      Pripravi predračun →
                    </Link>
                  )}
                  {status === "quote_requested" && (
                    <button disabled={busy === oid} onClick={() => setStatus(oid, "pending")}
                      className="bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-50">
                      Označi v obdelavo
                    </button>
                  )}
                  {(status === "quote_sent" || status === "pending") && (
                    <button disabled={busy === oid} onClick={() => setStatus(oid, "paid")}
                      className="bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-50">
                      Označi kot plačano
                    </button>
                  )}
                  {status === "paid" && (
                    <>
                      {shipFor === oid ? (
                        <div className="w-full flex flex-wrap gap-2 items-end">
                          <label className="text-xs">
                            <span className="block text-muted-foreground">Prevoznik</span>
                            <input value={trackingCar} onChange={(e) => setTrackingCar(e.target.value)} placeholder="Pošta Slovenije" className="border border-border px-2 py-1 text-xs" />
                          </label>
                          <label className="text-xs">
                            <span className="block text-muted-foreground">Št. sledenja</span>
                            <input value={trackingNum} onChange={(e) => setTrackingNum(e.target.value)} placeholder="RR123456789SI" className="border border-border px-2 py-1 text-xs font-mono" />
                          </label>
                          <button disabled={busy === oid} onClick={() => submitShip(oid)} className="bg-primary text-primary-foreground px-3 py-1.5 text-xs disabled:opacity-50">Potrdi odpošiljanje</button>
                          <button onClick={() => setShipFor(null)} className="border border-border px-3 py-1.5 text-xs">Prekliči</button>
                        </div>
                      ) : (
                        <button onClick={() => setShipFor(oid)} className="bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90">Odpošlji</button>
                      )}
                    </>
                  )}
                  {status === "shipped" && (
                    <button disabled={busy === oid} onClick={() => setStatus(oid, "delivered")}
                      className="bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-50">
                      Označi dostavljeno
                    </button>
                  )}
                  {status !== "canceled" && status !== "refunded" && status !== "shipped" && status !== "delivered" && (
                    <button disabled={busy === oid} onClick={() => setStatus(oid, "canceled")}
                      className="border border-border px-3 py-1.5 text-xs text-destructive hover:bg-muted disabled:opacity-50">
                      Prekliči
                    </button>
                  )}
                  {(status === "paid" || status === "shipped") && (
                    <button disabled={busy === oid} onClick={() => setStatus(oid, "refunded")}
                      className="border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
                      Označi povrnjeno
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="border border-border p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
    </div>
  );
}
